import React, { useState, useMemo } from 'react';
import { Calculator, TrendingUp, DollarSign, FileText, ToggleLeft, ToggleRight } from 'lucide-react';

interface TaxDepreciationDemoProps {
  totalPropertyValue: number;
}

const TaxDepreciationDemo: React.FC<TaxDepreciationDemoProps> = ({ 
  totalPropertyValue = 3000000 
}) => {
  const [holdingPeriod, setHoldingPeriod] = useState<number>(7);
  const [method, setMethod] = useState<'economic' | 'tax'>('tax');
  const [macrsType, setMacrsType] = useState<'GDS' | 'ADS'>('GDS');
  const [bonusDepreciation, setBonusDepreciation] = useState<number>(80);
  const [section179, setSection179] = useState<number>(0);
  const [taxRate, setTaxRate] = useState<number>(35);

  // Asset allocation (simplified)
  const assetAllocation = useMemo(() => ({
    building: totalPropertyValue * 0.48,      // 39-year MACRS
    equipment: totalPropertyValue * 0.08,     // 7-year MACRS
    siteWork: totalPropertyValue * 0.12,      // 15-year MACRS
  }), [totalPropertyValue]);

  // MACRS GDS depreciation rates (simplified)
  const macrsRates = {
    building: [2.461, 2.564, 2.564, 2.564, 2.564, 2.564, 2.564], // 39-year (first 7 years)
    equipment: [14.29, 24.49, 17.49, 12.49, 8.93, 8.92, 8.93], // 7-year
    siteWork: [5.0, 9.5, 8.55, 7.7, 6.93, 6.23, 5.9], // 15-year
  };

  // Calculate depreciation schedule
  const depreciationSchedule = useMemo(() => {
    const schedule = [];
    
    for (let year = 1; year <= holdingPeriod; year++) {
      let buildingDepr = 0;
      let equipmentDepr = 0;
      let siteWorkDepr = 0;

      if (method === 'tax') {
        // Apply bonus depreciation in year 1
        if (year === 1) {
          equipmentDepr = assetAllocation.equipment * (bonusDepreciation / 100);
          siteWorkDepr = assetAllocation.siteWork * (bonusDepreciation / 100);
          
          // Remaining basis after bonus
          const remainingEquipment = assetAllocation.equipment * (1 - bonusDepreciation / 100);
          const remainingSiteWork = assetAllocation.siteWork * (1 - bonusDepreciation / 100);
          
          // Add regular MACRS on remaining basis
          equipmentDepr += remainingEquipment * (macrsRates.equipment[year - 1] / 100);
          siteWorkDepr += remainingSiteWork * (macrsRates.siteWork[year - 1] / 100);
        } else {
          // Regular MACRS on remaining basis
          const remainingEquipment = assetAllocation.equipment * (1 - bonusDepreciation / 100);
          const remainingSiteWork = assetAllocation.siteWork * (1 - bonusDepreciation / 100);
          
          if (year - 1 < macrsRates.equipment.length) {
            equipmentDepr = remainingEquipment * (macrsRates.equipment[year - 1] / 100);
          }
          if (year - 1 < macrsRates.siteWork.length) {
            siteWorkDepr = remainingSiteWork * (macrsRates.siteWork[year - 1] / 100);
          }
        }
        
        // Building depreciation (39-year, no bonus)
        if (year - 1 < macrsRates.building.length) {
          buildingDepr = assetAllocation.building * (macrsRates.building[year - 1] / 100);
        }
        
        // Section 179 in year 1
        if (year === 1 && section179 > 0) {
          equipmentDepr += Math.min(section179, assetAllocation.equipment * 0.5);
        }
      } else {
        // Economic (straight-line)
        buildingDepr = assetAllocation.building / 39;
        equipmentDepr = assetAllocation.equipment / 7;
        siteWorkDepr = assetAllocation.siteWork / 15;
      }

      const totalDepr = buildingDepr + equipmentDepr + siteWorkDepr;
      const taxSavings = totalDepr * (taxRate / 100);

      schedule.push({
        year,
        building: Math.round(buildingDepr),
        equipment: Math.round(equipmentDepr),
        siteWork: Math.round(siteWorkDepr),
        total: Math.round(totalDepr),
        taxSavings: Math.round(taxSavings),
      });
    }

    return schedule;
  }, [holdingPeriod, method, macrsType, bonusDepreciation, section179, taxRate, assetAllocation]);

  // Summary calculations
  const summary = useMemo(() => {
    const totalDeductions = depreciationSchedule.reduce((sum, year) => sum + year.total, 0);
    const totalTaxSavings = depreciationSchedule.reduce((sum, year) => sum + year.taxSavings, 0);
    const firstYearDeduction = depreciationSchedule[0]?.total || 0;
    const firstYearSavings = depreciationSchedule[0]?.taxSavings || 0;
    
    return {
      totalDeductions,
      totalTaxSavings,
      firstYearDeduction,
      firstYearSavings,
      averageAnnual: Math.round(totalDeductions / holdingPeriod),
    };
  }, [depreciationSchedule, holdingPeriod]);

  // Asset allocation data for simple display
  const assetData = [
    { name: 'Building', value: assetAllocation.building, color: '#3B82F6', percentage: 60 },
    { name: 'Equipment', value: assetAllocation.equipment, color: '#10B981', percentage: 25 },
    { name: 'Site Work', value: assetAllocation.siteWork, color: '#F59E0B', percentage: 15 },
  ];

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-blue-100 rounded-lg">
          <Calculator className="h-6 w-6 text-blue-600" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-900">Tax Depreciation Analysis</h3>
          <p className="text-sm text-gray-600">Interactive investment tax benefit calculator</p>
        </div>
      </div>

      {/* Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
        {/* Holding Period */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Holding Period</label>
          <div className="flex gap-1">
            {[5, 7, 10].map((years) => (
              <button
                key={years}
                onClick={() => setHoldingPeriod(years)}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  holdingPeriod === years
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {years}y
              </button>
            ))}
          </div>
        </div>

        {/* Method Toggle */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Method</label>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setMethod(method === 'economic' ? 'tax' : 'economic')}
              className="flex items-center gap-2"
            >
              {method === 'tax' ? (
                <ToggleRight className="h-5 w-5 text-blue-600" />
              ) : (
                <ToggleLeft className="h-5 w-5 text-gray-400" />
              )}
              <span className="text-sm font-medium">
                {method === 'tax' ? 'Tax (MACRS)' : 'Economic'}
              </span>
            </button>
          </div>
        </div>

        {/* Sliders Container - stacked vertically on the right */}
        <div className="md:col-span-2 space-y-4">
          {/* Bonus Depreciation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bonus Depreciation
            </label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="0"
                max="100"
                step="10"
                value={bonusDepreciation}
                onChange={(e) => setBonusDepreciation(Number(e.target.value))}
                className="flex-1"
                disabled={method === 'economic'}
              />
              <span className="text-sm font-medium w-12">{bonusDepreciation}%</span>
            </div>
          </div>

          {/* Tax Rate */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tax Rate</label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="20"
                max="50"
                step="5"
                value={taxRate}
                onChange={(e) => setTaxRate(Number(e.target.value))}
                className="flex-1"
              />
              <span className="text-sm font-medium w-12">{taxRate}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="h-4 w-4 text-green-600" />
            <span className="text-sm font-medium text-green-800">Year 1 Deduction</span>
          </div>
          <div className="text-2xl font-bold text-green-900">
            {formatCurrency(summary.firstYearDeduction)}
          </div>
          <div className="text-sm text-green-700">
            Tax Savings: {formatCurrency(summary.firstYearSavings)}
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-800">Total Deductions</span>
          </div>
          <div className="text-2xl font-bold text-blue-900">
            {formatCurrency(summary.totalDeductions)}
          </div>
          <div className="text-sm text-blue-700">
            Over {holdingPeriod} years
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
          <div className="flex items-center gap-2 mb-2">
            <Calculator className="h-4 w-4 text-purple-600" />
            <span className="text-sm font-medium text-purple-800">Total Tax Savings</span>
          </div>
          <div className="text-2xl font-bold text-purple-900">
            {formatCurrency(summary.totalTaxSavings)}
          </div>
          <div className="text-sm text-purple-700">
            At {taxRate}% tax rate
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-50 to-orange-100 p-4 rounded-lg border border-orange-200">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="h-4 w-4 text-orange-600" />
            <span className="text-sm font-medium text-orange-800">Average Annual</span>
          </div>
          <div className="text-2xl font-bold text-orange-900">
            {formatCurrency(summary.averageAnnual)}
          </div>
          <div className="text-sm text-orange-700">
            Per year deduction
          </div>
        </div>
      </div>

      {/* Simplified Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Depreciation Schedule Table */}
        <div>
          <h4 className="text-lg font-semibold text-gray-900 mb-4">
            Annual Depreciation Schedule
          </h4>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2">Year</th>
                    <th className="text-right py-2">Building</th>
                    <th className="text-right py-2">Equipment</th>
                    <th className="text-right py-2">Site Work</th>
                    <th className="text-right py-2 font-bold">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {depreciationSchedule.map((row) => (
                    <tr key={row.year} className="border-b border-gray-100">
                      <td className="py-2 font-medium">{row.year}</td>
                      <td className="py-2 text-right text-blue-600">
                        ${(row.building / 1000).toFixed(0)}K
                      </td>
                      <td className="py-2 text-right text-green-600">
                        ${(row.equipment / 1000).toFixed(0)}K
                      </td>
                      <td className="py-2 text-right text-orange-600">
                        ${(row.siteWork / 1000).toFixed(0)}K
                      </td>
                      <td className="py-2 text-right font-bold">
                        ${(row.total / 1000).toFixed(0)}K
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Asset Allocation */}
        <div>
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Asset Allocation</h4>
          <div className="space-y-4">
            {assetData.map((item) => (
              <div key={item.name} className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-4 h-4 rounded-full" 
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="font-medium">{item.name}</span>
                  </div>
                  <span className="text-sm text-gray-600">{item.percentage}%</span>
                </div>
                <div className="text-lg font-bold text-gray-900">
                  {formatCurrency(item.value)}
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                  <div 
                    className="h-2 rounded-full transition-all duration-300"
                    style={{ 
                      backgroundColor: item.color, 
                      width: `${item.percentage}%` 
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="p-1 bg-yellow-100 rounded">
            <FileText className="h-4 w-4 text-yellow-600" />
          </div>
          <div>
            <h5 className="font-medium text-yellow-800 mb-1">Important Disclaimer</h5>
            <p className="text-sm text-yellow-700">
              This analysis is for illustrative purposes only and should not be considered tax advice. 
              Actual depreciation benefits may vary based on individual circumstances, tax law changes, 
              and specific asset classifications. Please consult with a qualified tax professional 
              for personalized guidance.
            </p>
          </div>
        </div>
      </div>

      {/* Action Buttons - Hidden per user request */}
      {/* <div className="flex gap-3 pt-4 border-t border-gray-200">
        <button className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors">
          Save Analysis
        </button>
        <button className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-200 transition-colors">
          Export Summary
        </button>
      </div> */}
    </div>
  );
};

export default TaxDepreciationDemo;
